import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Check,
  CheckSquare,
  FileText,
  Info,
  Layers,
  ListOrdered,
  Upload,
  X
} from 'lucide-react';
import { sectionsApi } from '../services/api';
import { Section as SectionModel } from '../types';

/**
 * Ausführliche Anleitung zum Aufbau der Importdateien.
 *
 * Die Seite beschreibt für jedes unterstützte Format Schritt für Schritt, wie eine Datei
 * anzulegen ist, was übernommen wird und was nicht – als Vorbereitung auf den Import.
 */

type GuideFormat = 'docx' | 'rtf' | 'html' | 'markdown' | 'csv';

const SEPARATOR = '*==============================*';

/** Variablentypen des Systems – „im CSV-Import“ zeigt, was die Spalte variablen_typen annimmt. */
const VALUE_TYPES: { key: string; label: string; meaning: string; csv: boolean }[] = [
  { key: 'TEXT', label: 'Text', meaning: 'Freies Textfeld – Standard, wenn nichts angegeben ist', csv: true },
  { key: 'NUMBER', label: 'Zahl', meaning: 'Ganze Zahlen und Dezimalzahlen', csv: true },
  { key: 'DATE', label: 'Datum', meaning: 'Datumsfeld mit Kalenderauswahl', csv: true },
  { key: 'CURRENCY', label: 'Währung (€)', meaning: 'Geldbetrag mit zwei Nachkommastellen', csv: true },
  { key: 'BOOLEAN', label: 'Ja/Nein', meaning: 'Ankreuzfeld', csv: true },
  { key: 'SELECT', label: 'Auswahl (Dropdown)', meaning: 'Auswahl aus vorgegebenen Optionen', csv: true },
  { key: 'FORMULA', label: 'Formel (berechnet)', meaning: 'Wird aus anderen Variablen berechnet', csv: false },
];

const CSV_VALUE_TYPES = VALUE_TYPES.filter(entry => entry.csv);

// ==================== Bausteine der Darstellung ====================

const Section = ({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) => (
  <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
      {icon}
      {title}
    </h2>
    <div className="space-y-4 text-sm text-gray-600">{children}</div>
  </section>
);

const Code = ({ children }: { children: React.ReactNode }) => (
  <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-[0.9em]">{children}</code>
);

const Sample = ({ caption, children }: { caption?: string; children: string }) => (
  <div>
    {caption && <p className="text-xs font-medium text-gray-500 mb-1">{caption}</p>}
    <pre className="bg-gray-900 text-green-300 rounded-lg p-4 overflow-x-auto text-xs whitespace-pre font-mono">{children}</pre>
  </div>
);

const Steps = ({ items }: { items: React.ReactNode[] }) => (
  <ol className="space-y-2">
    {items.map((item, index) => (
      <li key={index} className="flex gap-3">
        <span className="shrink-0 h-5 w-5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium flex items-center justify-center">
          {index + 1}
        </span>
        <span className="flex-1">{item}</span>
      </li>
    ))}
  </ol>
);

const Bullets = ({ items, tone = 'neutral' }: { items: React.ReactNode[]; tone?: 'yes' | 'no' | 'neutral' }) => (
  <ul className="space-y-1">
    {items.map((item, index) => (
      <li key={index} className="flex gap-2">
        {tone === 'yes' && <Check className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />}
        {tone === 'no' && <X className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
        {tone === 'neutral' && <span className="text-gray-400 shrink-0">•</span>}
        <span className="flex-1">{item}</span>
      </li>
    ))}
  </ul>
);

// ==================== Inhalte je Format ====================

interface FormatGuide {
  label: string;
  fileTypes: string;
  intro: React.ReactNode;
  steps: React.ReactNode[];
  supported: React.ReactNode[];
  unsupported: React.ReactNode[];
  sample?: { caption: string; code: string };
  notes?: React.ReactNode[];
}

const WORD_SAMPLE = `Name: Mietgegenstand
Abteil: Vertragsgegenstand

§ 1 Mietgegenstand              ← Formatvorlage „Überschrift 1“
Der Vermieter vermietet an den Mieter die Wohnung in <<adresse>>
bestehend aus <<anzahl_zimmer>> Zimmern.   ← „<<anzahl_zimmer>> Zimmern“ fett

•  Keller: <<keller>>            ← Aufzählung über die Listenschaltfläche
•  Stellplatz: <<stellplatz>>

${SEPARATOR}

Name: Mietzins
Abteil: Vergütung

§ 2 Mietzins                    ← Formatvorlage „Überschrift 1“
Der monatliche Mietzins beträgt <<mietzins>> EUR.`;

const HTML_SAMPLE = `<!DOCTYPE html>
<html lang="de">
<head><meta charset="utf-8"></head>
<body>
  <p>Name: Mietgegenstand</p>
  <p>Abteil: Vertragsgegenstand</p>

  <h1 style="color:#1f4e79">&sect; 1 Mietgegenstand</h1>
  <p style="text-align:justify">Der Vermieter vermietet die Wohnung in
    &lt;&lt;adresse&gt;&gt; mit <b>&lt;&lt;anzahl_zimmer&gt;&gt; Zimmern</b>.</p>
  <ul>
    <li>Keller: &lt;&lt;keller&gt;&gt;</li>
    <li>Stellplatz: &lt;&lt;stellplatz&gt;&gt;</li>
  </ul>

  <p>${SEPARATOR}</p>

  <p>Name: Mietzins</p>
  <p>Abteil: Verg&uuml;tung</p>
  <h1>&sect; 2 Mietzins</h1>
  <table>
    <tr><th>Position</th><th>Betrag</th></tr>
    <tr><td>Grundmiete</td><td>&lt;&lt;mietzins&gt;&gt; EUR</td></tr>
  </table>
</body>
</html>`;

const MARKDOWN_SAMPLE = `Name: Mietgegenstand
Abteil: Vertragsgegenstand

# § 1 Mietgegenstand

Der Vermieter vermietet die Wohnung in <<adresse>> mit
**<<anzahl_zimmer>> Zimmern**. Die Übergabe erfolgt *besenrein*.

- Keller: <<keller>>
  - Kellerabteil Nr. <<keller_nummer>>
- Stellplatz: <<stellplatz>>

${SEPARATOR}

Name: Mietzins
Abteil: Vergütung

# § 2 Mietzins

| Position       | Betrag                  |
| -------------- | ----------------------: |
| Grundmiete     | <<mietzins>> EUR        |
| Betriebskosten | <<betriebskosten>> EUR  |

> Rückstände werden mit <<verzugszinsen>> % p. a. verzinst.`;

const CSV_SAMPLE = `name|abteil|inhalt|variablen|variablen_typen|variablen_optionen|variablen_hilfe
Mietgegenstand|Vertragsgegenstand|Der Vermieter vermietet die Wohnung in <<adresse>>.|adresse|TEXT||Vollständige Adresse
Mietzins|Vergütung|Der Mietzins beträgt <<mietzins>> EUR, fällig am <<faelligkeit>>.|mietzins,faelligkeit|CURRENCY,SELECT|;1./5./15.|Monatsmiete,Fälligkeit wählen`;

const FORMAT_GUIDES: Record<GuideFormat, FormatGuide> = {
  docx: {
    label: 'Word',
    fileTypes: '.docx, .docm, .dotx',
    intro: (
      <>
        Der direkte Weg aus Microsoft Word. Wichtig ist, dass Sie Überschriften und Listen über die
        <strong> Funktionen von Word</strong> anlegen und nicht von Hand nachbauen – nur dann werden sie als
        Überschrift beziehungsweise Liste erkannt.
      </>
    ),
    steps: [
      <>Neues Dokument anlegen und am Ende als <Code>.docx</Code> speichern (nicht als altes <Code>.doc</Code>).</>,
      <>Je Bauteil zuerst die optionalen Kopfzeilen <Code>Name:</Code> und <Code>Abteil:</Code> als eigene Absätze schreiben.</>,
      <>Überschriften über <strong>Start → Formatvorlagen → „Überschrift 1“ bis „Überschrift 6“</strong> zuweisen.
        Nur größer und fett formatierter Text bleibt ein normaler Absatz.</>,
      <>Aufzählungen und Nummerierungen über die <strong>Listenschaltflächen</strong> erzeugen; Unterebenen mit Tab einrücken.
        Selbst getippte Zeichen wie <Code>-</Code> oder <Code>1.</Code> bleiben Text.</>,
      <>Tabellen über <strong>Einfügen → Tabelle</strong>. Damit die erste Zeile zur Kopfzeile wird, unter
        <strong> Tabellenlayout → Überschriftenzeile wiederholen</strong> aktivieren.</>,
      <>Bilder über <strong>Einfügen → Bilder</strong> einfügen und beim Einfügen <strong>„Einfügen“</strong> wählen
        (nicht „Mit Datei verknüpfen“), damit das Bild in der Datei enthalten ist.</>,
      <>Zwischen zwei Bauteile einen eigenen Absatz mit der Trennzeile <Code>{SEPARATOR}</Code> setzen.</>,
      <>Änderungsverfolgung abschließen (Änderungen annehmen) und Kommentare löschen – beides wird beim Import ignoriert.</>,
      <>Datei speichern, im Import hochladen und die Vorschau prüfen.</>,
    ],
    supported: [
      'Fett, kursiv, unterstrichen, durchgestrichen, hoch-/tiefgestellt, Kapitälchen',
      'Schriftart, Schriftgrad, Schriftfarbe, Texthervorhebung und Zellen-/Zeichenschattierung',
      'Überschriften aus den Formatvorlagen und aus Gliederungsebenen',
      'Ausrichtung, Einzüge (auch hängend), Abstand davor/danach, Zeilenabstand, „nicht trennen“',
      'Aufzählungen und nummerierte Listen mit Ebenen',
      'Tabellen mit Kopfzeile und Spaltenbreiten',
      'Hyperlinks, manuelle Seitenumbrüche, Seitenformat und Seitenränder',
      'Eingebettete Bilder (PNG, JPEG, GIF, WebP) bis 4 MB je Bild',
    ],
    unsupported: [
      'Kopf- und Fußzeilen, Fuß- und Endnoten, Kommentare',
      'Textfelder, Formen und WordArt',
      'Felder wie Seitenzahlen oder Datum (nur das sichtbare Ergebnis wird übernommen)',
      'Verknüpfte (nicht eingebettete) Bilder sowie alte WMF-/EMF-Grafiken',
      'Bilder innerhalb von Tabellenzellen – Sie erhalten dazu einen Hinweis',
      'Zeichenformate innerhalb von Tabellenzellen (Zellen sind reiner Text)',
      'Absatzrahmen und -schattierungen, mehrspaltiger Satz, Textmarken',
    ],
    sample: { caption: 'So sieht der Aufbau im Word-Dokument aus:', code: WORD_SAMPLE },
    notes: [
      <>Schriftarten werden auf die drei Standardschriften der PDF-Ausgabe abgebildet: Serifenschriften wie
        Times New Roman auf <Code>Times</Code>, dicktengleiche wie Courier auf <Code>Courier</Code>, alle übrigen
        (z. B. Calibri, Arial) auf <Code>Helvetica</Code>.</>,
      <>Ein Bauteil darf beliebig lang sein; die Trennzeile entscheidet allein über die Aufteilung.</>,
    ],
  },

  rtf: {
    label: 'RTF',
    fileTypes: '.rtf',
    intro: (
      <>
        Rich Text Format schreiben Word, WordPad und LibreOffice über <strong>Speichern unter → Rich-Text-Format</strong>.
        Es gelten dieselben Regeln wie für Word-Dateien.
      </>
    ),
    steps: [
      <>Dokument wie gewohnt in Word, WordPad oder LibreOffice erstellen.</>,
      <>Überschriften über die Formatvorlagen („Überschrift 1“ … bzw. „Heading 1“ …) zuweisen.</>,
      <>Aufzählungen über die Listenfunktion erzeugen – auch die ältere Schreibweise von WordPad wird erkannt.</>,
      <>Tabellen und Bilder wie in Word einfügen; Bilder als PNG oder JPEG einbetten.</>,
      <>Zwischen zwei Bauteile eine eigene Zeile mit <Code>{SEPARATOR}</Code> setzen.</>,
      <>Über <strong>Speichern unter</strong> das Format <strong>Rich-Text-Format (*.rtf)</strong> wählen.</>,
    ],
    supported: [
      'Alle Zeichen- und Absatzformate wie bei Word',
      'Überschriften über Formatvorlagen und Gliederungsebenen',
      'Aufzählungen und nummerierte Listen, auch aus älteren Programmen',
      'Tabellen mit Kopfzeile und Spaltenbreiten, Seitenumbrüche, Seiteneinrichtung',
      'Hyperlinks aus Feldern sowie eingebettete PNG- und JPEG-Bilder',
      'Umlaute und Sonderzeichen in allen üblichen Schreibweisen der Datei',
    ],
    unsupported: [
      'Kopf- und Fußzeilen, Fuß- und Endnoten, Kommentare, Textfelder',
      'WMF-/EMF-Grafiken (typisch für ältere Word-Versionen) – Sie erhalten einen Hinweis',
      'Bilder innerhalb von Tabellenzellen',
      'Absatzrahmen und -schattierungen',
    ],
    sample: {
      caption: 'Ausschnitt einer RTF-Datei (so sieht die Datei im Texteditor aus):',
      code: `{\\rtf1\\ansi\\ansicpg1252\\deff0
{\\fonttbl{\\f0\\fswiss Calibri;}}
\\pard\\fs22 Name: Mietgegenstand\\par
Abteil: Vertragsgegenstand\\par
\\pard\\sa120\\outlinelevel0\\b\\fs28 \\u167? 1 Mietgegenstand\\b0\\fs22\\par
\\pard\\sa120\\qj Der Vermieter vermietet die Wohnung in <<adresse>>
mit {\\b <<anzahl_zimmer>> Zimmern}.\\par
\\pard\\qc ${SEPARATOR}\\par
\\pard\\fs22 Name: Mietzins\\par
}`,
    },
    notes: [
      <>Sie müssen RTF nicht von Hand schreiben – speichern Sie einfach aus Ihrem Textprogramm heraus.
        Die Beispieldatei im Import zeigt den vollständigen Aufbau.</>,
    ],
  },

  html: {
    label: 'HTML',
    fileTypes: '.html, .htm, .xhtml',
    intro: (
      <>
        Geeignet für Inhalte aus Web-Editoren und für die Web-Exporte von Word („Speichern unter → Webseite“) und
        Google Docs („Herunterladen → Webseite“). Entscheidend: Die Formatierung muss aus den Tags selbst oder aus
        <Code>style</Code>-Angaben direkt am Element kommen.
      </>
    ),
    steps: [
      <>Datei mit <Code>{'<meta charset="utf-8">'}</Code> anlegen und in UTF-8 speichern.</>,
      <>Je Bauteil die Kopfzeilen als eigene Absätze schreiben: <Code>{'<p>Name: …</p>'}</Code>, <Code>{'<p>Abteil: …</p>'}</Code>.</>,
      <>Überschriften mit <Code>{'<h1>'}</Code> bis <Code>{'<h6>'}</Code>, Absätze mit <Code>{'<p>'}</Code> auszeichnen.</>,
      <>Listen mit <Code>{'<ul>'}</Code>/<Code>{'<ol>'}</Code> und <Code>{'<li>'}</Code>, Verschachtelung für Unterebenen.</>,
      <>Tabellen mit <Code>{'<table>'}</Code>, <Code>{'<tr>'}</Code> und <Code>{'<td>'}</Code>; die Kopfzeile mit <Code>{'<th>'}</Code>.</>,
      <>Formatierung entweder über Tags (<Code>{'<b>'}</Code>, <Code>{'<i>'}</Code>, <Code>{'<u>'}</Code>) oder über
        <Code>{' style="…"'}</Code> direkt am Element.</>,
      <>Zwischen zwei Bauteile einen eigenen Absatz mit der Trennzeile setzen: <Code>{`<p>${SEPARATOR}</p>`}</Code>.</>,
    ],
    supported: [
      <><Code>{'<b> <strong> <i> <em> <u> <ins> <s> <strike> <del> <sup> <sub> <code> <mark> <a> <font>'}</Code></>,
      <><Code>{'<p> <div> <h1>–<h6> <ul> <ol> <li> <table> <tr> <th> <td> <blockquote> <pre> <hr> <br> <img>'}</Code></>,
      <>Formatangaben am Element: <Code>font-weight</Code>, <Code>font-style</Code>, <Code>text-decoration</Code>,
        <Code>color</Code>, <Code>background-color</Code>, <Code>font-size</Code>, <Code>font-family</Code>,
        <Code>vertical-align</Code>, <Code>font-variant</Code></>,
      <>Absatzangaben: <Code>text-align</Code>, <Code>margin</Code>, <Code>padding-left</Code>, <Code>text-indent</Code>,
        <Code>line-height</Code>, <Code>page-break-before</Code></>,
      'Bilder als eingebettete data:-Adresse oder als http(s)-Adresse',
      'Links mit http(s)-, mailto- oder tel-Adresse',
    ],
    unsupported: [
      <><Code>{'<style>'}</Code>-Blöcke, CSS-Klassen und eingebundene Stylesheets – Formatierung muss am Element stehen</>,
      <>Lokale Bildpfade wie <Code>{'src="bild.png"'}</Code> (Bild einbetten oder auf eine http(s)-Adresse legen)</>,
      <>Skripte, Formulare, eingebettete Rahmen (<Code>{'<iframe>'}</Code>), SVG-Grafiken im Text</>,
      'Links mit anderen Protokollen (z. B. javascript:) werden verworfen, der Text bleibt erhalten',
      'Bilder innerhalb von Tabellenzellen',
    ],
    sample: { caption: 'Vollständige HTML-Datei mit zwei Bauteilen:', code: HTML_SAMPLE },
    notes: [
      <>Word-Web-Exporte funktionieren, weil Word die Formatierung direkt an die Elemente schreibt; die dabei
        erzeugten Listen (<Code>mso-list</Code>) werden ebenfalls erkannt.</>,
      <>Zeilenumbrüche und Einrückungen im Quelltext werden wie im Browser zusammengefasst – für einen echten
        Umbruch <Code>{'<br>'}</Code> verwenden.</>,
    ],
  },

  markdown: {
    label: 'Markdown',
    fileTypes: '.md, .markdown, .mdown, .txt',
    intro: (
      <>
        Der schnellste Weg, viele Bauteile in einer einfachen Textdatei zu erfassen – schreibbar in jedem
        Texteditor, ohne Textverarbeitung.
      </>
    ),
    steps: [
      <>Textdatei in UTF-8 anlegen (jeder Editor, z. B. Notepad++, VS Code oder der Windows-Editor).</>,
      <>Je Bauteil die Kopfzeilen <Code>Name:</Code> und <Code>Abteil:</Code> in eigene Zeilen schreiben.</>,
      <>Überschriften mit <Code>#</Code> beginnen: <Code># Ebene 1</Code>, <Code>## Ebene 2</Code> und so weiter.</>,
      <>Absätze durch eine <strong>Leerzeile</strong> trennen. Aufeinanderfolgende Zeilen ohne Leerzeile werden zu
        einem Absatz verbunden; ein harter Umbruch entsteht durch zwei Leerzeichen am Zeilenende.</>,
      <>Listen mit <Code>-</Code> oder <Code>1.</Code>; Unterebenen um zwei Leerzeichen einrücken.</>,
      <>Tabellen mit <Code>|</Code> und einer Trennzeile aus Bindestrichen; <Code>---:</Code> richtet die Spalte rechts aus.</>,
      <>Zwischen zwei Bauteile eine eigene Zeile mit <Code>{SEPARATOR}</Code> setzen.</>,
    ],
    supported: [
      <>Auszeichnungen: <Code>**fett**</Code>, <Code>*kursiv*</Code>, <Code>~~durchgestrichen~~</Code>, <Code>`Code`</Code> –
        auch ineinander verschachtelt</>,
      <>Überschriften <Code># … ######</Code> sowie unterstrichene Überschriften (<Code>===</Code> / <Code>---</Code>)</>,
      <>Listen mit Ebenen, Zitate mit <Code>&gt;</Code>, Trennlinien mit <Code>---</Code>, Codeblöcke mit <Code>```</Code></>,
      <>Tabellen mit Kopfzeile und Spaltenausrichtung</>,
      <>Links <Code>[Text](https://…)</Code> und Bilder <Code>![Text](data:… )</Code></>,
      <>Maskierte Zeichen: <Code>\\*</Code> erzeugt ein Sternchen statt einer Auszeichnung</>,
    ],
    unsupported: [
      'HTML-Tags in der Markdown-Datei werden nicht ausgewertet, sondern bleiben Text',
      'Fuß- und Endnoten, Referenz-Links, Definitionslisten',
      'Bilder mit lokalen Dateipfaden (data:- oder http(s)-Adresse verwenden)',
      'Formatierung innerhalb von Tabellenzellen – Auszeichnungen werden dort zu reinem Text aufgelöst',
    ],
    sample: { caption: 'Vollständige Markdown-Datei mit zwei Bauteilen:', code: MARKDOWN_SAMPLE },
    notes: [
      <>Platzhalter <Code>{'<<name>>'}</Code> bleiben unangetastet – Markdown wertet die spitzen Klammern nicht aus.</>,
      <>Auch eine reine <Code>.txt</Code>-Datei ohne jede Auszeichnung lässt sich so importieren: Jeder Absatz wird
        übernommen, Trennzeilen und Kopfzeilen gelten genauso.</>,
    ],
  },

  csv: {
    label: 'CSV',
    fileTypes: '.csv, .txt',
    intro: (
      <>
        Für viele kurze Bauteile <strong>ohne Formatierung</strong>. Jede Zeile ist ein Bauteil, die Spalten sind
        durch <Code>|</Code> getrennt. Trennzeilen und Kopfzeilen gibt es hier nicht – die Aufteilung erfolgt über die Zeilen.
      </>
    ),
    steps: [
      <>Erste Zeile als Kopfzeile mit den Spaltennamen anlegen:
        <Code>name|abteil|inhalt|variablen|variablen_typen|variablen_optionen|variablen_hilfe</Code>.</>,
      <>Je Bauteil eine Zeile schreiben. Pflichtspalten sind <Code>name</Code>, <Code>abteil</Code> und <Code>inhalt</Code>.</>,
      <>Die Abteilnamen müssen genau so bereits im System vorhanden sein (Groß-/Kleinschreibung ist egal).</>,
      <>Platzhalter wie gewohnt als <Code>{'<<name>>'}</Code> in den Inhalt schreiben; zusätzliche Variablen in der
        Spalte <Code>variablen</Code> kommagetrennt ergänzen.</>,
      <>Typen in <Code>variablen_typen</Code> in derselben Reihenfolge angeben:{' '}
        {CSV_VALUE_TYPES.map((entry, index) => (
          <React.Fragment key={entry.key}>{index > 0 && ', '}<Code>{entry.key}</Code></React.Fragment>
        ))}. <Code>FORMULA</Code> lässt sich erst danach im Bauteil einstellen.</>,
      <>Für <Code>SELECT</Code> die Auswahl in <Code>variablen_optionen</Code> hinterlegen: Variablen mit <Code>;</Code>
        trennen, die einzelnen Optionen mit <Code>/</Code>.</>,
      <>Datei in UTF-8 speichern und im Reiter „CSV-Import“ hochladen oder den Inhalt direkt einfügen.</>,
    ],
    supported: [
      'Name, Abteil und Inhalt je Bauteil',
      'Automatisch erkannte Platzhalter sowie zusätzlich angegebene Variablen',
      'Variablentypen, Auswahloptionen und Hilfetexte',
      'Umlaute und Sonderzeichen (UTF-8)',
    ],
    unsupported: [
      'Jede Form von Formatierung (fett, Überschriften, Listen, Tabellen, Bilder)',
      'Zeilenumbrüche innerhalb eines Bauteils – eine Zeile ist genau ein Bauteil',
      <>Das Zeichen <Code>|</Code> innerhalb eines Textes (es trennt immer die Spalten)</>,
      'Bauteile ohne Abteil – die Spalte abteil ist Pflicht',
    ],
    sample: { caption: 'CSV-Datei mit Kopfzeile und zwei Bauteilen:', code: CSV_SAMPLE },
    notes: [
      <>In Excel lässt sich die Datei als CSV speichern; ersetzen Sie anschließend in einem Texteditor das
        Trennzeichen (meist <Code>;</Code>) durch <Code>|</Code>. Ebenso gut können Sie die Datei direkt im
        Texteditor schreiben.</>,
      <>Brauchen Sie Formatierung, verwenden Sie stattdessen den Dokument-Import mit Word, RTF, HTML oder Markdown.</>,
    ],
  },
};

const FORMAT_ORDER: GuideFormat[] = ['docx', 'rtf', 'html', 'markdown', 'csv'];

const STRUCTURE_SAMPLE = `Name: Mietgegenstand            ← optional: Name des Bauteils
Abteil: Vertragsgegenstand      ← optional: vorhandenes Abteil
Aktiv: nein                     ← optional: Bauteil deaktiviert anlegen

§ 1 Mietgegenstand              ← Inhalt des Bauteils (Überschrift)
Der Vermieter vermietet die Wohnung in <<adresse>> …

${SEPARATOR}   ← trennt die Bauteile

Name: Mietzins
Abteil: Vergütung

§ 2 Mietzins
Der monatliche Mietzins beträgt <<mietzins>> EUR …`;

const MESSAGES: { message: string; meaning: string; solution: string }[] = [
  {
    message: 'Abteil "…" nicht gefunden',
    meaning: 'Die Kopfzeile „Abteil:“ nennt ein Abteil, das es im System nicht gibt.',
    solution: 'Abteil unter „Abteile“ anlegen oder die Schreibweise in der Datei korrigieren.',
  },
  {
    message: 'Ungültige Platzhalter: …',
    meaning: 'Ein Platzhalter enthält unerlaubte Zeichen, etwa Umlaute, Leerzeichen oder Bindestriche.',
    solution: 'Namen auf Buchstaben, Ziffern und _ beschränken und mit einem Buchstaben beginnen lassen.',
  },
  {
    message: 'Das Bauteil enthält keinen Text',
    meaning: 'Zwischen zwei Trennzeilen steht kein Inhalt, oder es sind nur Kopfzeilen vorhanden.',
    solution: 'Inhalt ergänzen oder überzählige Trennzeilen entfernen.',
  },
  {
    message: 'Die Datei enthält keinen auswertbaren Inhalt',
    meaning: 'Die Datei ist leer oder besteht nur aus Elementen, die nicht übernommen werden.',
    solution: 'Inhalt in den Fließtext stellen – nicht in Kopf-/Fußzeilen oder Textfelder.',
  },
  {
    message: 'Die Datei ist kein gültiges Word-Dokument',
    meaning: 'Die Datei ist beschädigt oder liegt im alten .doc-Format vor.',
    solution: 'In Word über „Speichern unter“ als .docx speichern.',
  },
  {
    message: 'Nicht unterstütztes Dateiformat',
    meaning: 'Die Dateiendung gehört zu keinem der unterstützten Formate.',
    solution: 'Datei als .docx, .rtf, .html oder .md speichern.',
  },
  {
    message: 'Die Datei ist zu groß (maximal 20 MB)',
    meaning: 'Meist verursachen sehr große Bilder die Dateigröße.',
    solution: 'Bilder verkleinern oder die Datei in mehrere Importe aufteilen.',
  },
  {
    message: 'Ein Bild konnte nicht übernommen werden …',
    meaning: 'Das Bild liegt in einem nicht unterstützten Format vor (z. B. WMF/EMF) oder ist größer als 4 MB.',
    solution: 'Bild als PNG oder JPEG erneut einfügen und die Dateigröße verringern.',
  },
  {
    message: 'Ein Bild in einer Tabellenzelle konnte nicht übernommen werden',
    meaning: 'Tabellenzellen enthalten reinen Text, deshalb bleiben Bilder darin außen vor.',
    solution: 'Bild ober- oder unterhalb der Tabelle platzieren.',
  },
  {
    message: 'Bauteil mit diesem Namen existiert bereits',
    meaning: 'Die Option „Vorhandene Namen überspringen“ ist aktiv und der Name ist vergeben.',
    solution: 'Bauteil umbenennen oder die Option abwählen, wenn ein zweites Bauteil gewollt ist.',
  },
];

const CHECKLIST: string[] = [
  'Alle benötigten Abteile sind unter „Abteile“ angelegt.',
  'Zwischen allen Bauteilen steht eine eigene Trennzeile.',
  'Die Kopfzeilen stehen ganz oben im jeweiligen Bauteil, jede in einer eigenen Zeile.',
  'Alle Platzhalter heißen nur mit Buchstaben, Ziffern und _ und beginnen mit einem Buchstaben.',
  'Überschriften und Listen sind über die Funktionen des Programms angelegt (Word, RTF).',
  'Bilder sind eingebettet, kleiner als 4 MB und liegen nicht in Tabellenzellen.',
  'Die Datei ist kleiner als 20 MB und in UTF-8 gespeichert (HTML, Markdown, CSV).',
  'Die Vorschau im Import zeigt alle Bauteile mit dem erwarteten Namen und Abteil.',
];

/**
 * Die tatsächlich angelegten Abteile dieses Systems.
 *
 * Der Import legt keine Abteile an, deshalb zeigt die Anleitung die Namen, die in der Kopfzeile
 * „Abteil:“ beziehungsweise in der CSV-Spalte „abteil“ verwendet werden können.
 */
function AvailableSections() {
  const [sections, setSections] = useState<SectionModel[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    sectionsApi.getAll()
      .then(response => { if (active) setSections(response.sections || []); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);

  return (
    <Section title="Aktuell vorhandene Abteile" icon={<Layers className="h-5 w-5 mr-2 text-primary-500" />}>
      <p>
        Genau diese Namen können Sie in der Kopfzeile <Code>Abteil:</Code> und in der CSV-Spalte <Code>abteil</Code>{' '}
        verwenden; Groß-/Kleinschreibung spielt dabei keine Rolle. Der Import legt selbst kein Abteil an.
      </p>

      {!sections && !failed && <p className="text-gray-400">Abteile werden geladen …</p>}

      {failed && (
        <p className="text-red-600">
          Die Abteile konnten nicht geladen werden. Die vollständige Liste finden Sie unter{' '}
          <Link to="/admin/sections" className="underline">Abteile</Link>.
        </p>
      )}

      {sections?.length === 0 && (
        <div className="p-4 bg-amber-50 rounded-lg text-amber-700">
          Es ist noch kein Abteil angelegt. Legen Sie zuerst unter{' '}
          <Link to="/admin/sections" className="underline">Abteile</Link> mindestens eines an – sonst lassen sich
          Bauteile nur ohne Abteil importieren.
        </div>
      )}

      {!!sections?.length && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {sections.map(section => (
            <li key={section.id} className="border border-gray-200 rounded-lg px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <Code>{section.name}</Code>
                <span className="text-xs text-gray-400 shrink-0">{section._count?.blocks ?? 0} Bauteile</span>
              </div>
              {section.description && <p className="mt-1 text-xs text-gray-500">{section.description}</p>}
            </li>
          ))}
        </ul>
      )}

      <p>
        Fehlt ein Abteil, legen Sie es vor dem Import unter{' '}
        <Link to="/admin/sections" className="text-primary-600 hover:underline">Abteile</Link> an. Bauteile mit einem
        unbekannten Abteil meldet die Vorschau als fehlerhaft und überspringt sie.
      </p>
    </Section>
  );
}

/** Anleitung, „Wird übernommen“ und „Wird nicht übernommen“ eines Formats. */
const FormatGuideView = ({ guide }: { guide: FormatGuide }) => (
  <div className="space-y-6">
    <Section title={`${guide.label}-Datei anlegen`} icon={<Upload className="h-5 w-5 mr-2 text-primary-500" />}>
      <p className="text-gray-500 text-xs">Dateiendungen: {guide.fileTypes}</p>
      <p>{guide.intro}</p>
      <Steps items={guide.steps} />
      {guide.sample && <Sample caption={guide.sample.caption}>{guide.sample.code}</Sample>}
      {guide.notes?.map((note, index) => (
        <div key={index} className="flex gap-2 p-3 bg-gray-50 rounded-lg">
          <Info className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
          <span>{note}</span>
        </div>
      ))}
    </Section>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title={`Wird übernommen (${guide.label})`} icon={<Check className="h-5 w-5 mr-2 text-green-600" />}>
        <Bullets tone="yes" items={guide.supported} />
      </Section>
      <Section title={`Wird nicht übernommen (${guide.label})`} icon={<X className="h-5 w-5 mr-2 text-red-500" />}>
        <Bullets tone="no" items={guide.unsupported} />
      </Section>
    </div>
  </div>
);

// ==================== Seite ====================

export default function BlocksImportGuide() {
  const [format, setFormat] = useState<GuideFormat>('docx');
  /** Alle Formate untereinander – für den Ausdruck oder die Weitergabe als PDF. */
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/blocks/import" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zum Import
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Anleitung: Importdateien richtig aufbauen</h1>
        <p className="mt-1 text-sm text-gray-500">
          Wie eine Datei aufgebaut sein muss, damit alle Bauteile mit ihrer Formatierung ankommen
        </p>
      </div>

      <div className="space-y-6">
        {/* Grundaufbau */}
        <Section title="Grundaufbau jeder Datei" icon={<BookOpen className="h-5 w-5 mr-2 text-primary-500" />}>
          <p>
            Eine Datei enthält <strong>ein oder beliebig viele Bauteile</strong>. Der Aufbau ist in allen
            Dokumentformaten gleich – nur die Schreibweise unterscheidet sich. Der CSV-Import folgt eigenen
            Regeln, die im Reiter „CSV“ beschrieben sind.
          </p>
          <Sample>{STRUCTURE_SAMPLE}</Sample>
          <div className="p-4 bg-blue-50 rounded-lg text-blue-700">
            <p className="font-medium mb-1">Nichts wird ohne Ihre Bestätigung gespeichert</p>
            <p className="text-blue-600">
              Nach dem Hochladen zeigt der Import zuerst eine Vorschau aller gefundenen Bauteile mit Formatierung,
              Abteil und Variablen. Erst der Klick auf „Importieren“ legt die Bauteile an.
            </p>
          </div>
        </Section>

        {/* Trennzeile */}
        <Section title="Die Trennzeile zwischen zwei Bauteilen" icon={<ListOrdered className="h-5 w-5 mr-2 text-primary-500" />}>
          <p>
            Die Trennzeile steht <strong>allein in einer eigenen Zeile beziehungsweise einem eigenen Absatz</strong>.
            Sie besteht aus einem Sternchen, mindestens zwei Gleichheitszeichen und einem Sternchen. Wie viele
            Gleichheitszeichen Sie verwenden, spielt keine Rolle; Leerzeichen werden ignoriert. Die Trennzeile
            selbst erscheint nicht im Bauteil.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-green-700 mb-1">Richtig</p>
              <Bullets
                tone="yes"
                items={[<Code>*===================*</Code>, <Code>*==*</Code>, <Code>*  ====  *</Code>]}
              />
            </div>
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Falsch</p>
              <Bullets
                tone="no"
                items={[
                  <><Code>*=*</Code> – zu wenige Gleichheitszeichen</>,
                  <><Code>--- Trennung ---</Code> – andere Zeichen</>,
                  <>Text und Trennzeile in derselben Zeile</>,
                ]}
              />
            </div>
          </div>
        </Section>

        {/* Kopfzeilen */}
        <Section title="Kopfzeilen: Name, Abteil und Aktiv" icon={<FileText className="h-5 w-5 mr-2 text-primary-500" />}>
          <p>
            Die Kopfzeilen stehen <strong>ganz am Anfang eines Bauteils</strong> – vor dem ersten Inhalt und jede in
            einer eigenen Zeile. Sie werden ausgewertet und erscheinen nicht im Bauteiltext. Sobald die erste
            gewöhnliche Zeile folgt, endet der Kopfbereich.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left border border-gray-200 rounded-lg">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 border-b border-gray-200">Kopfzeile</th>
                  <th className="px-3 py-2 border-b border-gray-200">Bedeutung</th>
                  <th className="px-3 py-2 border-b border-gray-200">Wenn sie fehlt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-3 py-2 align-top"><Code>Name:</Code> <span className="text-gray-400">(auch Titel:, Bauteil:)</span></td>
                  <td className="px-3 py-2 align-top">Name des Bauteils, höchstens 200 Zeichen</td>
                  <td className="px-3 py-2 align-top">Die erste Textzeile des Bauteils wird zum Namen</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 align-top"><Code>Abteil:</Code> <span className="text-gray-400">(auch Abschnitt:, Sektion:)</span></td>
                  <td className="px-3 py-2 align-top">Vorhandenes Abteil, Groß-/Kleinschreibung egal</td>
                  <td className="px-3 py-2 align-top">Das im Import gewählte Ziel-Abteil wird verwendet</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 align-top"><Code>Aktiv:</Code></td>
                  <td className="px-3 py-2 align-top"><Code>ja</Code> oder <Code>nein</Code></td>
                  <td className="px-3 py-2 align-top">Das Bauteil wird aktiv angelegt</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Ein Abteil, das in der Datei genannt wird, muss vorher unter <strong>Abteile</strong> angelegt sein –
            sonst meldet die Vorschau dieses Bauteil als fehlerhaft und überspringt es beim Import. Alle übrigen
            Bauteile werden trotzdem importiert.
          </p>
        </Section>

        {/* Vorhandene Abteile */}
        <AvailableSections />

        {/* Platzhalter */}
        <Section title="Platzhalter für Variablen" icon={<Info className="h-5 w-5 mr-2 text-primary-500" />}>
          <p>
            Platzhalter schreiben Sie als <Code>{'<<name>>'}</Code> mitten in den Text – auch in Tabellenzellen und
            Listen. Jeder gefundene Platzhalter wird beim Import als Variable des Bauteils angelegt; mehrfach
            verwendete Platzhalter ergeben eine einzige Variable.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-green-700 mb-1">Gültige Namen</p>
              <Bullets
                tone="yes"
                items={[
                  <Code>{'<<adresse>>'}</Code>,
                  <Code>{'<<anzahl_zimmer>>'}</Code>,
                  <Code>{'<<mietzins2>>'}</Code>,
                ]}
              />
            </div>
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Ungültige Namen</p>
              <Bullets
                tone="no"
                items={[
                  <><Code>{'<<Größe>>'}</Code> – keine Umlaute</>,
                  <><Code>{'<<anzahl zimmer>>'}</Code> – kein Leerzeichen</>,
                  <><Code>{'<<2_zimmer>>'}</Code> – muss mit einem Buchstaben beginnen</>,
                  <><Code>{'<<miet-zins>>'}</Code> – kein Bindestrich</>,
                ]}
              />
            </div>
          </div>
          <p>
            Erlaubt sind Buchstaben (ohne Umlaute), Ziffern und der Unterstrich; der Name muss mit einem Buchstaben
            beginnen.
          </p>
          <p>
            Jede Variable hat einen Typ. Beim Dokument-Import (Word, RTF, HTML, Markdown) entsteht jede Variable
            als{' '}<Code>TEXT</Code> ohne Pflichtangabe; im CSV-Import geben Sie den Typ in der Spalte{' '}
            <Code>variablen_typen</Code> an. Beschriftung, Hilfetext und Typ lassen sich danach jederzeit im Bauteil
            ändern. Diese Typen gibt es:
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left border border-gray-200 rounded-lg">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 border-b border-gray-200">Typ</th>
                  <th className="px-3 py-2 border-b border-gray-200">Im Bauteil</th>
                  <th className="px-3 py-2 border-b border-gray-200">Bedeutung</th>
                  <th className="px-3 py-2 border-b border-gray-200">Im CSV-Import</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {VALUE_TYPES.map(entry => (
                  <tr key={entry.key}>
                    <td className="px-3 py-2 align-top"><Code>{entry.key}</Code></td>
                    <td className="px-3 py-2 align-top">{entry.label}</td>
                    <td className="px-3 py-2 align-top">{entry.meaning}</td>
                    <td className="px-3 py-2 align-top">{entry.csv ? 'ja' : 'nein – nur im Bauteil einstellbar'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Formatabhängiger Teil */}
        <div>
          <div className="mb-4 border-b border-gray-200 flex flex-wrap items-end justify-between gap-3">
            <nav className="-mb-px flex flex-wrap gap-x-6" aria-label="Format">
              {FORMAT_ORDER.map(entry => (
                <button
                  key={entry}
                  onClick={() => { setFormat(entry); setShowAll(false); }}
                  aria-current={!showAll && format === entry ? 'page' : undefined}
                  className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium ${
                    !showAll && format === entry
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {FORMAT_GUIDES[entry].label}
                </button>
              ))}
            </nav>
            <button
              onClick={() => setShowAll(current => !current)}
              className="mb-2 inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-lg hover:bg-gray-50"
            >
              <ListOrdered className="h-4 w-4 mr-2" />
              {showAll ? 'Nur ein Format anzeigen' : 'Alle Formate anzeigen (zum Ausdrucken)'}
            </button>
          </div>

          {showAll ? (
            <div className="space-y-6">
              {FORMAT_ORDER.map(entry => <FormatGuideView key={entry} guide={FORMAT_GUIDES[entry]} />)}
            </div>
          ) : (
            <FormatGuideView guide={FORMAT_GUIDES[format]} />
          )}
        </div>

        {/* Checkliste */}
        <Section title="Checkliste vor dem Import" icon={<CheckSquare className="h-5 w-5 mr-2 text-primary-500" />}>
          <ul className="space-y-2">
            {CHECKLIST.map((item, index) => (
              <li key={index} className="flex gap-2">
                <span className="mt-0.5 h-4 w-4 border border-gray-300 rounded-sm shrink-0" aria-hidden="true" />
                <span className="flex-1">{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Meldungen */}
        <Section title="Meldungen im Import und was zu tun ist" icon={<Info className="h-5 w-5 mr-2 text-primary-500" />}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left border border-gray-200 rounded-lg">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 border-b border-gray-200">Meldung</th>
                  <th className="px-3 py-2 border-b border-gray-200">Bedeutung</th>
                  <th className="px-3 py-2 border-b border-gray-200">Lösung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {MESSAGES.map(entry => (
                  <tr key={entry.message}>
                    <td className="px-3 py-2 align-top font-medium text-gray-700">{entry.message}</td>
                    <td className="px-3 py-2 align-top">{entry.meaning}</td>
                    <td className="px-3 py-2 align-top">{entry.solution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Fehlerhafte Bauteile halten den Import nicht auf: Sie werden in der Vorschau rot gekennzeichnet und
            übersprungen, alle übrigen Bauteile werden angelegt.
          </p>
        </Section>

        <div className="flex justify-end">
          <Link
            to="/admin/blocks/import"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <Upload className="h-4 w-4 mr-2" />
            Zum Import
          </Link>
        </div>
      </div>
    </div>
  );
}
